import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

// material-ui
import {
    Avatar,
    Button,
    Chip,
    Divider,
    Grid,
    Stack,
    Table,
    TableCell,
    TableContainer,
    TableHead,
    TableBody,
    TableRow,
    Typography
} from '@mui/material';

import { useTheme } from '@mui/material/styles';

// project imports
import SubCard from 'ui-component/cards/SubCard';
import MainCard from 'ui-component/cards/MainCard';
import { gridSpacing } from 'store/constant';
import {
    GetSportsType,
    GetUserType,
    CapFirstChar,
    ChangeCurrencyFormat,
    GetBookingStatus,
    GetPaymentStatus
} from 'store/TextFormat';
import DEFAULT_USER_IMAGE from 'assets/images/users/default_user.png';
import useApi from 'hooks/useApi';
import TransactionService from 'services/TransactionService';
import MasterService from 'services/MasterService';
import VenueService from 'services/VenueService';
import TasksCard from './BoardBookings/TaskCard';
import { GetTransferStatus } from 'store/TextFormat';

// third party
import moment from 'moment';

const sxDivider = { borderColor: 'primary.light' };

const BookingDetails = () => {

    const theme = useTheme();
    const location = useLocation();
    const path = location.pathname;
    const navigate = useNavigate();
    const [sportsTypes, setSportsType] = useState([]);
    const { venue_uuid, bookingType, bookingUuid } = useParams();
    const [bookingDetails, setBookingDetails] = useState(null);
    const [transactionDetails, setTransactionDetails] = useState(null);
    const [usedDuration, setUsedDuration] = useState(0);
    const [usedBookings, setUsedBookings] = useState([]);
    const [showTransferStatus, setTransferStatus] = useState(false);

    const getDiff = (onDate, offDate) => {
        const startTime = moment(onDate.replace('T', ' ').replace('.000Z', ''), 'YYYY-MM-DD HH:mm:ss').format('YYYY-MM-DD HH:mm:ss');
        const endTime = moment(offDate.replace('T', ' ').replace('.000Z', ''), 'YYYY-MM-DD HH:mm:ss').format('YYYY-MM-DD HH:mm:ss');
        const duration = moment.duration(moment(endTime).diff(startTime));
        const hours = parseInt(duration.asHours());
        const minutes = parseInt(duration.asMinutes()) % 60;
        const seconds = parseInt(duration.asSeconds()) % 60;
        const hoursLabel = hours <= 1 ? 'hr' : 'hrs'
        const minutessLabel = minutes <= 1 ? 'min' : 'mins'
        const secondsLabel = seconds <= 1 ? 'sec' : 'secs'
        return `${hours} ${hoursLabel} ${minutes} ${minutessLabel} ${seconds} ${secondsLabel}`;
    };

    const formatSecondsToMinutes = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        const minutessLabel = minutes <= 1 ? 'min' : 'mins'
        const secondsLabel = seconds <= 1 ? 'sec' : 'secs'
        return `${minutes} ${minutessLabel} ${remainingSeconds.toString().padStart(2, '0')} ${secondsLabel}`;
    };

    const handleDetailsSuccess = (response) => {
        const data = response.data;
        setTransactionDetails(data);
    };

    const getStHandler = (response) => {
        const data = response.data;
        setSportsType(data);
        if (bookingType === 'board') {
            getBoardDetailsApi.execute(bookingUuid);
        } else {
            getCourtDetailsApi.execute(bookingUuid);
        }
    };

    const handleOnClick = () => {
        const pathparts = path.split('/');
        if (path.startsWith('/board/view')) {
            const boardUuid = pathparts[3];
            navigate(`/board/view/${boardUuid}`);
        } else if (path.startsWith('/venue/view')) {
            const venueUuid = pathparts[3];
            navigate(`/venue/view/${venueUuid}`);
        } else {
            navigate(`/bookings`);
        }
    };

    const getBoardUsageLogHandler = (response) => {
        const data = response.data;
        setUsedBookings(data.rows);
        setUsedDuration(data.totalDuration);
    };

    const getCourtDetailsApi = useApi(TransactionService.getSlotBookingDetails, handleDetailsSuccess);
    const getBoardDetailsApi = useApi(TransactionService.getBoardBookingDetails, handleDetailsSuccess);
    const getSportsTypeListApi = useApi(MasterService.getDropdownSportsType, getStHandler)
    const getBoardLogDetailsByUuid = useApi(VenueService.getBoardUsageLogByUuid, getBoardUsageLogHandler)

    useEffect(() => {
        getSportsTypeListApi.execute();
        getBoardLogDetailsByUuid.execute(bookingUuid);
    }, []);

    useEffect(() => {
        if (transactionDetails) {
            setBookingDetails(
                bookingType === 'court' ? transactionDetails.courtBookingDetails : transactionDetails.boardBookingDetails
            );
            setTransferStatus(transactionDetails.courtBookingDetails ? true : false);
        }
    }, [transactionDetails]);

    return (

        <MainCard
            title="Booking Details"
            secondary={
                (<><Button
                    variant="contained"
                    color="secondary"
                    size='small'
                    onClick={() => handleOnClick()}
                >
                    Back
                </Button>
                </>)
            }
            sx={{ border: 0, padding: '0px', margin: '0px' }}
        >
            {
                transactionDetails && bookingDetails ? (
                    <Grid container spacing={gridSpacing}>
                        <Grid item xs={7}>
                            <SubCard
                                secondary={
                                    <>
                                        <Stack direction="row" spacing={1}>
                                            <Typography variant="subtitle1">Booked By </Typography>
                                            <Typography variant="body2" sx={{ marginTop: '2px !important' }}>
                                                : {`${CapFirstChar(bookingDetails.created_user_firstname)} ${bookingDetails.created_user_lastname ? bookingDetails.created_user_lastname : ''} / ${GetUserType(bookingDetails.created_user_type)}`}
                                            </Typography>
                                        </Stack>
                                        <Stack direction="row" spacing={1}>
                                            <Typography variant="subtitle1">Booked On </Typography>
                                            <Typography variant="body2" sx={{ marginTop: '2px !important' }}>
                                                : {moment(bookingDetails.created_at).format('DD-MM-YYYY hh:mm A')}
                                            </Typography>
                                        </Stack>
                                    </>
                                }
                                title={
                                    <>
                                        {
                                            transactionDetails.courtBookingDetails ? (
                                                <Stack direction="row" spacing={1}>
                                                    <Typography variant="subtitle1">Court Booking Id </Typography>
                                                    <Typography variant="body2" sx={{ marginTop: '2px !important' }}>
                                                        : {transactionDetails.courtBookingDetails.slot_id}
                                                    </Typography>
                                                </Stack>
                                            ) : ('')
                                        }
                                        {
                                            transactionDetails.boardBookingDetails ? (
                                                <Stack direction="row" spacing={1}>
                                                    <Typography variant="subtitle1">Board Booking Id </Typography>
                                                    <Typography variant="body2" sx={{ marginTop: '2px !important' }}>
                                                        : {transactionDetails.boardBookingDetails.code}
                                                    </Typography>
                                                </Stack>
                                            ) : ('')
                                        }
                                        <Stack direction="row" spacing={1}>
                                            <Typography variant="subtitle1">Customer </Typography>
                                            <Typography variant="body2" sx={{ marginTop: '2px !important' }}>
                                                : {`${CapFirstChar(bookingDetails.booked_user_firstname)} ${bookingDetails.booked_user_lastname ? bookingDetails.booked_user_lastname : ''}`} ({bookingDetails.booked_user_code})
                                            </Typography>
                                        </Stack>
                                    </>
                                }
                            >
                                <Grid container spacing={gridSpacing}>
                                    <Grid item xs={12}>
                                        <Grid container spacing={gridSpacing}>
                                            <Grid item>
                                                <Stack spacing={2}>
                                                    <Typography variant="h4">Booking Details</Typography>
                                                    <Stack spacing={0}>
                                                        <Stack direction="row" spacing={1}>
                                                            <Typography variant="subtitle1">Venue Name </Typography>
                                                            <Typography variant="body2" sx={{ marginTop: '2px !important' }}>
                                                                : {bookingDetails.venue_name}
                                                            </Typography>
                                                        </Stack>
                                                        <Stack direction="row" spacing={1}>
                                                            <Typography variant="subtitle1">Court Name </Typography>
                                                            <Typography variant="body2" sx={{ marginTop: '2px !important' }}>
                                                                : {bookingDetails.turf_name}
                                                            </Typography>
                                                        </Stack>
                                                        {
                                                            transactionDetails.boardBookingDetails ? (
                                                                <Stack direction="row" spacing={1}>
                                                                    <Typography variant="subtitle1">Board Name </Typography>
                                                                    <Typography variant="body2" sx={{ marginTop: '2px !important' }}>
                                                                        : {transactionDetails.boardBookingDetails.board_name}</Typography>
                                                                </Stack>
                                                            ) : (<></>)
                                                        }
                                                    </Stack>
                                                </Stack>
                                            </Grid>
                                            {
                                                transactionDetails.courtBookingDetails ? (
                                                    <Grid item>
                                                        <Stack spacing={2}>
                                                            <Typography variant="h4">&nbsp;</Typography>
                                                            <Stack spacing={0}>
                                                                <Stack direction="row" spacing={1}>
                                                                    <Typography variant="subtitle1">Court Duration </Typography>
                                                                    <Typography variant="body2" sx={{ marginTop: '2px !important' }}>
                                                                        : {bookingDetails.duration} mins
                                                                    </Typography>
                                                                </Stack>
                                                                <Stack direction="row" spacing={1}>
                                                                    <Typography variant="subtitle1">Start Time </Typography>
                                                                    <Typography variant="body2" sx={{ marginTop: '2px !important' }}>
                                                                        : {moment(bookingDetails.start_date).format('DD-MM-YYYY')} {moment(bookingDetails.start_date + ' ' + bookingDetails.start_time + ':00', 'YYYY-MM-DD HH:mm:ss').format('hh:mm A')}
                                                                    </Typography>
                                                                </Stack>
                                                                <Stack direction="row" spacing={1}>
                                                                    <Typography variant="subtitle1">End Time </Typography>
                                                                    <Typography variant="body2" sx={{ marginTop: '2px !important' }}>
                                                                        : {moment(bookingDetails.end_date).format('DD-MM-YYYY')} {moment(bookingDetails.start_date + ' ' + bookingDetails.start_time + ':00', 'YYYY-MM-DD HH:mm:ss').add(parseInt(bookingDetails.duration), 'minutes').format('hh:mm A')}
                                                                    </Typography>
                                                                </Stack>
                                                            </Stack>
                                                        </Stack>
                                                    </Grid>
                                                ) : ('')
                                            }
                                            {
                                                transactionDetails.boardBookingDetails ? (
                                                    <Grid item>
                                                        <Stack spacing={2}>
                                                            <Typography variant="h4">&nbsp;</Typography>
                                                            <Stack spacing={0}>
                                                                <Stack direction="row" spacing={1}>
                                                                    <Typography variant="subtitle1">Board Duration </Typography>
                                                                    <Typography variant="body2" sx={{ marginTop: '2px !important' }}>
                                                                        : {transactionDetails.boardBookingDetails.duration} mins
                                                                    </Typography>
                                                                </Stack>
                                                                <Stack direction="row" spacing={1}>
                                                                    <Typography variant="subtitle1">Start Time </Typography>
                                                                    <Typography variant="body2" sx={{ marginTop: '2px !important' }}>
                                                                        : {moment(transactionDetails.boardBookingDetails.start_date).format('DD-MM-YYYY')} {moment(transactionDetails.boardBookingDetails.start_date + ' ' + transactionDetails.boardBookingDetails.start_time + ':00', 'YYYY-MM-DD HH:mm:ss').format('hh:mm A')}
                                                                    </Typography>
                                                                </Stack>
                                                                <Stack direction="row" spacing={1}>
                                                                    <Typography variant="subtitle1">End Time </Typography>
                                                                    <Typography variant="body2" sx={{ marginTop: '2px !important' }}>
                                                                        : {moment(transactionDetails.boardBookingDetails.end_date.replace('T', ' ').replace('.000Z', '')).format('DD-MM-YYYY')} {moment(transactionDetails.boardBookingDetails.start_date + ' ' + transactionDetails.boardBookingDetails.start_time + ':00', 'YYYY-MM-DD HH:mm:ss').add(parseInt(transactionDetails.boardBookingDetails.duration), 'minutes').format('hh:mm A')}
                                                                    </Typography>
                                                                </Stack>
                                                            </Stack>
                                                        </Stack>
                                                    </Grid>
                                                ) : (<></>)
                                            }
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Divider sx={sxDivider} />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Grid container spacing={gridSpacing}>
                                            <Grid item xs={12} sm={6} md={4}>
                                                <Stack spacing={2}>
                                                    {/* <Typography variant="h4"></Typography> */}
                                                    <Stack spacing={0}>
                                                        <Stack direction="row" spacing={1}>
                                                            <Typography variant="subtitle1">Payment Type :</Typography>
                                                            <Typography variant="subtitle1" sx={{ marginTop: '0px !important', marginBottom: '4px !important' }}>
                                                                {
                                                                    bookingDetails.payment_type === 'offline' ? (
                                                                        <Chip
                                                                            label={CapFirstChar(bookingDetails.payment_type)}
                                                                            size="small"
                                                                            sx={{
                                                                                background:
                                                                                    theme.palette.mode === 'dark'
                                                                                        ? theme.palette.dark.main
                                                                                        : theme.palette.primary.light + 60,
                                                                                color: theme.palette.primary.dark
                                                                            }}
                                                                        />
                                                                    ) : bookingDetails.payment_status === 'na' ? '-' : (
                                                                        <Chip
                                                                            label={CapFirstChar(bookingDetails.payment_type)}
                                                                            size="small"
                                                                            sx={{
                                                                                background:
                                                                                    theme.palette.mode === 'dark'
                                                                                        ? theme.palette.dark.main
                                                                                        : theme.palette.secondary.light + 60,
                                                                                color: theme.palette.secondary.dark
                                                                            }}
                                                                        />
                                                                    )
                                                                }
                                                            </Typography>
                                                        </Stack>
                                                        <Stack direction="row" spacing={1}>
                                                            <Typography variant="subtitle1">Payment Mode :</Typography>
                                                            <Typography variant="subtitle1" sx={{ marginTop: '0px !important', marginBottom: '4px !important' }}>
                                                                {
                                                                    bookingDetails.payment_mode === 'full' ? (
                                                                        <Chip
                                                                            label={CapFirstChar(bookingDetails.payment_mode)}
                                                                            size="small"
                                                                            sx={{
                                                                                background:
                                                                                    theme.palette.mode === 'dark'
                                                                                        ? theme.palette.dark.main
                                                                                        : theme.palette.primary.light + 60,
                                                                                color: theme.palette.primary.dark
                                                                            }}
                                                                        />
                                                                    ) : bookingDetails.payment_mode === 'na' ? 'NA' : (
                                                                        <Chip
                                                                            label={CapFirstChar(bookingDetails.payment_mode)}
                                                                            size="small"
                                                                            sx={{
                                                                                background:
                                                                                    theme.palette.mode === 'dark'
                                                                                        ? theme.palette.dark.main
                                                                                        : theme.palette.secondary.light + 60,
                                                                                color: theme.palette.secondary.dark
                                                                            }}
                                                                        />
                                                                    )
                                                                }
                                                            </Typography>
                                                        </Stack>
                                                        {
                                                            bookingDetails.sports_type ? (
                                                                <Stack direction="row" spacing={1}>
                                                                    <Typography variant="subtitle1">Sports Type :</Typography>
                                                                    <Typography variant="subtitle1" sx={{ marginTop: '2px !important' }}>
                                                                        {GetSportsType(sportsTypes, bookingDetails.sports_type)}
                                                                    </Typography>
                                                                </Stack>
                                                            ) : ('')
                                                        }
                                                    </Stack>
                                                </Stack>
                                            </Grid>
                                            <Grid item xs={12} sm={6} md={4}>
                                                <Stack spacing={2}>
                                                    {/* <Typography variant="h4">Status</Typography> */}
                                                    <Stack spacing={0}>
                                                        <Stack direction="row" spacing={1}>
                                                            <Typography variant="subtitle1">Booking Status :</Typography>
                                                            <Typography variant="subtitle1" sx={{ marginTop: '0px !important', marginBottom: '4px !important' }}>
                                                                <Chip
                                                                    label={GetBookingStatus(bookingDetails.booking_status)}
                                                                    size="small"
                                                                    sx={{
                                                                        background:
                                                                            bookingDetails.booking_status === 'confirmed' ? theme.palette.success.light + 60 :
                                                                                bookingDetails.booking_status === 'cancelled' ? theme.palette.error.light + 60 :
                                                                                    bookingDetails.booking_status === 'blocked' || bookingDetails.booking_status === 'reserved' ? theme.palette.warning.light + 60 :
                                                                                        theme.palette.info.light + 60,
                                                                        color: bookingDetails.booking_status === 'confirmed' ? theme.palette.success.dark :
                                                                            bookingDetails.booking_status === 'cancelled' ? theme.palette.error.dark :
                                                                                bookingDetails.booking_status === 'blocked' || bookingDetails.booking_status === 'reserved' ? theme.palette.warning.dark :
                                                                                    theme.palette.info.dark,
                                                                    }}
                                                                />
                                                            </Typography>
                                                        </Stack>
                                                        <Stack direction="row" spacing={1}>
                                                            <Typography variant="subtitle1">Payment Status :</Typography>
                                                            {bookingDetails.payment_status === 'na' ? <Typography variant="subtitle1"> - </Typography> : (
                                                                <Typography variant="body2" sx={{ marginTop: '4px !important' }}>
                                                                    {
                                                                        bookingDetails.booking_status === 'blocked' ? (
                                                                            <Typography variant="subtitle1" sx={{ marginTop: '0px !important' }}>NA</Typography>
                                                                        ) : (
                                                                            <Chip
                                                                                label={GetPaymentStatus(bookingDetails.payment_status)}
                                                                                size="small"
                                                                                sx={{
                                                                                    background:
                                                                                        bookingDetails.payment_status === 'confirmed' ? theme.palette.success.light + 60 :
                                                                                            bookingDetails.payment_status === 'cancelled' ? theme.palette.error.light + 60 :
                                                                                                bookingDetails.payment_status === 'reserved' ? theme.palette.info.light + 60 :
                                                                                                    theme.palette.warning.light + 60,
                                                                                    color: bookingDetails.payment_status === 'confirmed' ? theme.palette.success.dark :
                                                                                        bookingDetails.payment_status === 'cancelled' ? theme.palette.error.dark :
                                                                                            bookingDetails.payment_status === 'reserved' ? theme.palette.info.dark :
                                                                                                theme.palette.warning.dark,
                                                                                }}
                                                                            />
                                                                        )
                                                                    }
                                                                    {bookingDetails.payment_status === 'refunded' ? `₹${transactionDetails.refund_amount}(${bookingDetails.refund_percentage}%)` : ''}
                                                                </Typography>)}

                                                        </Stack>{
                                                            showTransferStatus ? (
                                                                <Stack direction="row" spacing={1}>
                                                                    <Typography variant="subtitle1">Transfer Status :</Typography>
                                                                    <Typography variant="body2" sx={{ marginTop: '4px !important' }}>
                                                                        {
                                                                            bookingDetails.booking_status === 'blocked' ? (
                                                                                <Typography variant="subtitle1" sx={{ marginTop: '0px !important' }}>NA</Typography>
                                                                            ) : (
                                                                                <Chip
                                                                                    label={GetTransferStatus(transactionDetails.transfer_status)}
                                                                                    size="small"
                                                                                    sx={{
                                                                                        background:
                                                                                            transactionDetails.transfer_status === 'completed' || transactionDetails.transfer_status === 'reversed' ? theme.palette.success.light + 60 :
                                                                                                transactionDetails.transfer_status === 'cancelled' ? theme.palette.error.light + 60 :
                                                                                                    theme.palette.warning.light + 60,
                                                                                        color: transactionDetails.transfer_status === 'completed' || transactionDetails.transfer_status === 'reversed' ? theme.palette.success.dark :
                                                                                            bookingDetails.transfer_status === 'cancelled' ? theme.palette.error.dark :
                                                                                                theme.palette.warning.dark,
                                                                                    }}
                                                                                />
                                                                            )
                                                                        }
                                                                    </Typography>
                                                                </Stack>
                                                            ) : <></>
                                                        }
                                                    </Stack>
                                                </Stack>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <SubCard>
                                            <Grid container justifyContent="flex-end" spacing={gridSpacing}>
                                                <Grid item sm={6} md={5}>
                                                    <Grid container spacing={2} sx={{
                                                        mx: 0,
                                                        mb: 3,
                                                        px: 1,
                                                        bgcolor: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.primary.light
                                                    }}>
                                                        <Grid item xs={12}>
                                                            <Grid container spacing={1}>
                                                                {
                                                                    bookingDetails.actual_slot_price > 0 && (
                                                                        <>
                                                                            <Grid item xs={9}>
                                                                                <Typography align="right" variant="subtitle1">
                                                                                    Original Amount : &#8377;
                                                                                </Typography>
                                                                            </Grid>
                                                                            <Grid item xs={3}>
                                                                                <Typography align="right" variant="body2" sx={{ marginTop: '2px !important' }}>
                                                                                    {bookingDetails.actual_slot_price > 0 ? ChangeCurrencyFormat(bookingDetails.actual_slot_price) : 0.00}
                                                                                </Typography>
                                                                            </Grid>
                                                                        </>
                                                                    )
                                                                }
                                                                <Grid item xs={9}>
                                                                    <Typography align="right" variant="subtitle1">
                                                                        {bookingDetails.payment_mode === 'full' ? 'Sub Total' : 'Advance'} : &#8377;
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid item xs={3}>
                                                                    <Typography align="right" variant="body2" sx={{ marginTop: '2px !important' }}>
                                                                        {bookingDetails.subtotal > 0 ? ChangeCurrencyFormat(bookingDetails.subtotal) : 0.00}
                                                                    </Typography>
                                                                </Grid>
                                                                {
                                                                    bookingDetails.scoreboard_amount > 0 && bookingDetails.scoreboard_amount > 0 ? (
                                                                        <>
                                                                            <Grid item xs={9}>
                                                                                <Typography align="right" variant="subtitle1">
                                                                                    Board Amount : &#8377;
                                                                                </Typography>
                                                                            </Grid>
                                                                            <Grid item xs={3}>
                                                                                <Typography align="right" variant="body2" sx={{ marginTop: '2px !important' }}>
                                                                                    {bookingDetails ? ChangeCurrencyFormat(bookingDetails.scoreboard_amount) : 0.00}
                                                                                </Typography>
                                                                            </Grid>
                                                                        </>
                                                                    ) : ('')
                                                                }
                                                                {
                                                                    bookingDetails.payment_mode === 'cash' ? ('') : bookingDetails.convenience_fee > 0 ? (
                                                                        <>
                                                                            <Grid item xs={9}>
                                                                                <Typography align="right" variant="subtitle1">
                                                                                    Convenience Fee : &#8377;
                                                                                </Typography>
                                                                            </Grid>
                                                                            <Grid item xs={3}>
                                                                                <Typography align="right" variant="body2" sx={{ marginTop: '2px !important' }}>
                                                                                    {bookingDetails ? ChangeCurrencyFormat(bookingDetails.convenience_fee) : 0.00}
                                                                                </Typography>
                                                                            </Grid>
                                                                        </>
                                                                    ) : ('')
                                                                }
                                                                {
                                                                    bookingDetails.discount_amount > 0 ? (
                                                                        <>
                                                                            <Grid item xs={9}>
                                                                                <Typography align="right" variant="subtitle1">
                                                                                    Discount : &#8377;
                                                                                </Typography>
                                                                            </Grid>
                                                                            <Grid item xs={3}>
                                                                                <Typography align="right" variant="body2" sx={{ marginTop: '2px !important' }}>
                                                                                    {bookingDetails ? ChangeCurrencyFormat(bookingDetails.discount_amount) : 0.00}
                                                                                </Typography>
                                                                            </Grid>

                                                                        </>
                                                                    ) : ('')
                                                                }
                                                            </Grid>
                                                        </Grid>
                                                        <Grid item xs={12}>
                                                            <Divider sx={{ bgcolor: 'dark.main' }} />
                                                        </Grid>
                                                        <Grid item xs={12}>
                                                            <Grid container spacing={1}>
                                                                <Grid item xs={9}>
                                                                    <Typography align="right" color="primary" variant="subtitle1">
                                                                        Total : &#8377;
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid item xs={3}>
                                                                    <Typography align="right" color="primary" variant="subtitle1">
                                                                        {bookingDetails.total > 0 ? ChangeCurrencyFormat(bookingDetails.total) : 0.00}
                                                                    </Typography>
                                                                </Grid>
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </SubCard>
                                    </Grid>
                                </Grid>
                            </SubCard>
                        </Grid>
                        <Grid item xs={12} md={5}>
                            <TasksCard title="Booking Log" history={transactionDetails.history} />
                        </Grid>
                        <Grid item xs={12}>
                            <SubCard title={`Board Usage (${formatSecondsToMinutes(usedDuration)})`} >
                                {usedBookings.length ? (<TableContainer>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ pl: 3 }}>#</TableCell>
                                                <TableCell sx={{ whiteSpace: 'nowrap', paddingLeft: '70px' }}>Scored By</TableCell>
                                                <TableCell sx={{ whiteSpace: 'nowrap', paddingLeft: '70px' }}>Match ID</TableCell>
                                                <TableCell sx={{ whiteSpace: 'nowrap', paddingLeft: '70px' }}>Team A</TableCell>
                                                <TableCell sx={{ whiteSpace: 'nowrap', paddingLeft: '70px' }}>Team B</TableCell>
                                                <TableCell sx={{ whiteSpace: 'nowrap', paddingLeft: '80px' }}>Start Time</TableCell>
                                                <TableCell sx={{ whiteSpace: 'nowrap', paddingLeft: '80px' }}>End Time</TableCell>
                                                <TableCell sx={{ whiteSpace: 'nowrap', paddingLeft: '80px' }}>Used Duration</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {(usedBookings.map((row, index) =>
                                                <TableRow hover key={index}>
                                                    <TableCell sx={{ pl: 3 }}>{index + 1}</TableCell>
                                                    <TableCell sx={{ whiteSpace: 'nowrap', paddingLeft: '70px' }}>
                                                        <Grid container spacing={1} alignItems="center">
                                                            <Grid item spacing={1}>
                                                                <Avatar alt="User" src={row.uu_image ? row.uu_image : DEFAULT_USER_IMAGE} />
                                                            </Grid>
                                                            <Grid>
                                                                <Typography align="left" variant="subtitle1" component="div">
                                                                    {row.uu_firstname} {row.booked_user_lastname}
                                                                </Typography>
                                                                <Typography align="left" variant="subtitle2">
                                                                    {row.uu_code} / {GetUserType(row.uu_type)}
                                                                </Typography>
                                                            </Grid>
                                                        </Grid>
                                                    </TableCell>
                                                    <TableCell sx={{ whiteSpace: 'nowrap', paddingLeft: '70px' }}>{row.match_code}</TableCell>
                                                    <TableCell sx={{ whiteSpace: 'nowrap', paddingLeft: '80px' }}>
                                                        <Grid container spacing={2} alignItems="center">
                                                            <Grid item xs zeroMinWidth>
                                                                <Typography align="left" variant="subtitle1" component="div">
                                                                    {row.team_a_name}
                                                                </Typography>
                                                                <Typography align="left" variant="subtitle2" noWrap>
                                                                    {row.team_a_code}
                                                                </Typography>
                                                            </Grid>
                                                        </Grid>
                                                    </TableCell>
                                                    <TableCell sx={{ whiteSpace: 'nowrap', paddingLeft: '80px' }}>
                                                        <Grid container spacing={2} alignItems="center">
                                                            <Grid item xs zeroMinWidth>
                                                                <Typography align="left" variant="subtitle1" component="div">
                                                                    {row.team_b_name}
                                                                </Typography>
                                                                <Typography align="left" variant="subtitle2" noWrap>
                                                                    {row.team_b_code}
                                                                </Typography>
                                                            </Grid>
                                                        </Grid>
                                                    </TableCell>
                                                    <TableCell sx={{ whiteSpace: 'nowrap', paddingLeft: '80px' }}>
                                                        <Grid container spacing={2} alignItems="center">
                                                            <Grid item xs zeroMinWidth>
                                                                <Typography align="left" variant="subtitle1" component="div">
                                                                    {moment(row.start_time.replace('T', ' ').replace('.000Z', '')).format('DD-MM-YYYY')}
                                                                </Typography>
                                                                <Typography align="left" variant="subtitle2" noWrap>
                                                                    {moment(row.start_time.replace('T', ' ').replace('.000Z', '')).format('HH:mm:ss')}
                                                                </Typography>
                                                            </Grid>
                                                        </Grid>
                                                    </TableCell>
                                                    <TableCell sx={{ whiteSpace: 'nowrap', paddingLeft: '80px' }}>
                                                        {
                                                            row.end_time ? (
                                                                <Grid container spacing={2} alignItems="center">
                                                                    <Grid item xs zeroMinWidth>
                                                                        <Typography align="left" variant="subtitle1" component="div">
                                                                            {moment(row.end_time.replace('T', ' ').replace('.000Z', '')).format('DD-MM-YYYY')}
                                                                        </Typography>
                                                                        <Typography align="left" variant="subtitle2" noWrap>
                                                                            {moment(row.end_time.replace('T', ' ').replace('.000Z', '')).format('HH:mm:ss')}
                                                                        </Typography>
                                                                    </Grid>
                                                                </Grid>
                                                            ) : ''
                                                        }
                                                    </TableCell>
                                                    <TableCell sx={{ whiteSpace: 'nowrap', paddingLeft: '80px' }}>
                                                        {row.end_time ? getDiff(row.start_time, row.end_time) : ''}
                                                    </TableCell>
                                                </TableRow>))
                                            }
                                        </TableBody>
                                    </Table>
                                </TableContainer>)
                                    : <Typography variant='h3'>This Board Is Not Used</Typography>}
                            </SubCard>
                        </Grid>
                    </Grid>
                ) : (<></>)
            }
        </MainCard>
    );
};

export default BookingDetails;
